/**
 * Generic dual-handle range filter — single source of truth for all numeric
 * filters across Fantasy Academy dashboards.
 *
 * Props (matches RangeFilter type from product brief):
 *   id, label, minValue, maxValue, selectedMin, selectedMax, step, unit,
 *   format ("number" | "percent" | "currency" | "points" | "minutes"),
 *   onChange({selectedMin, selectedMax})
 *
 * Renders:
 *   - label + selected min/max numeric inputs (precise editing)
 *   - dual-handle slider (visual)
 *   - reset button
 */
import React, { useCallback, useEffect, useState } from "react";
import { ArrowCounterClockwise } from "@phosphor-icons/react";
import * as SliderPrimitive from "@radix-ui/react-slider";

const FORMATTERS = {
    number: (v) => v.toString(),
    percent: (v) => `${Number(v).toFixed(1)}%`,
    currency: (v) => `£${Number(v).toFixed(1)}m`,
    points: (v) => `${Math.round(v)} pts`,
    minutes: (v) => `${Math.round(v)} min`,
};

function format(value, fmt, unit) {
    if (value === undefined || value === null || Number.isNaN(value)) return "—";
    const fn = FORMATTERS[fmt] || FORMATTERS.number;
    if (fmt === "number" && unit) return `${fn(value)}${unit}`;
    return fn(value);
}

export default function RangeFilter({
    id,
    label,
    minValue,
    maxValue,
    selectedMin,
    selectedMax,
    step = 1,
    unit = "",
    format: fmt = "number",
    onChange,
    testid,
    defaults,
}) {
    const [localMin, setLocalMin] = useState(selectedMin);
    const [localMax, setLocalMax] = useState(selectedMax);

    useEffect(() => { setLocalMin(selectedMin); }, [selectedMin]);
    useEffect(() => { setLocalMax(selectedMax); }, [selectedMax]);

    const commitMin = useCallback((v) => {
        let next = Number(v);
        if (Number.isNaN(next)) return;
        next = Math.max(minValue, Math.min(next, localMax));
        setLocalMin(next);
        onChange?.({ selectedMin: next, selectedMax: localMax });
    }, [minValue, localMax, onChange]);

    const commitMax = useCallback((v) => {
        let next = Number(v);
        if (Number.isNaN(next)) return;
        next = Math.min(maxValue, Math.max(next, localMin));
        setLocalMax(next);
        onChange?.({ selectedMin: localMin, selectedMax: next });
    }, [maxValue, localMin, onChange]);

    const reset = useCallback(() => {
        const dMin = defaults?.selectedMin ?? minValue;
        const dMax = defaults?.selectedMax ?? maxValue;
        setLocalMin(dMin);
        setLocalMax(dMax);
        onChange?.({ selectedMin: dMin, selectedMax: dMax });
    }, [defaults, minValue, maxValue, onChange]);

    const handleSliderChange = useCallback(([nextMin, nextMax]) => {
        setLocalMin(nextMin);
        setLocalMax(nextMax);
    }, []);

    const handleSliderCommit = useCallback(([nextMin, nextMax]) => {
        onChange?.({ selectedMin: nextMin, selectedMax: nextMax });
    }, [onChange]);

    const range = maxValue - minValue || 1;
    const leftPct = ((localMin - minValue) / range) * 100;
    const rightPct = 100 - ((localMax - minValue) / range) * 100;

    return (
        <div className="border border-white/10 bg-black/30 px-3 py-2 space-y-2" data-testid={testid || `range-${id}`}>
            <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/60">
                    {label}
                </span>
                <button
                    type="button"
                    onClick={reset}
                    data-testid={`${testid || `range-${id}`}-reset`}
                    className="font-mono text-[9px] uppercase tracking-widest text-white/40 hover:text-fa-cream flex items-center gap-1"
                    title="Reset to default"
                >
                    <ArrowCounterClockwise size={10} weight="bold" />
                    Reset
                </button>
            </div>

            <div className="flex items-center gap-2">
                <input
                    type="number"
                    min={minValue}
                    max={localMax}
                    step={step}
                    value={localMin}
                    onChange={(e) => setLocalMin(Number(e.target.value))}
                    onBlur={(e) => commitMin(e.target.value)}
                    data-testid={`${testid || `range-${id}`}-min-input`}
                    className="w-20 bg-black/60 border border-white/10 focus:border-fa-cream/60 outline-none text-white font-mono text-xs px-2 py-1 tabular-nums"
                    aria-label={`${label} minimum`}
                />
                <SliderPrimitive.Root
                    className="relative flex w-full touch-none select-none items-center h-6 flex-1"
                    min={minValue}
                    max={maxValue}
                    step={step}
                    minStepsBetweenThumbs={0}
                    value={[localMin, localMax]}
                    onValueChange={handleSliderChange}
                    onValueCommit={handleSliderCommit}
                    data-testid={`${testid || `range-${id}`}-slider`}
                >
                    <SliderPrimitive.Track className="relative h-1 w-full grow bg-white/10 overflow-hidden">
                        <SliderPrimitive.Range className="absolute h-full bg-fa-cream" />
                    </SliderPrimitive.Track>
                    <SliderPrimitive.Thumb
                        className="block h-4 w-4 rounded-full bg-fa-cream border-2 border-black shadow cursor-grab active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fa-cream/50"
                        data-testid={`${testid || `range-${id}`}-min-thumb`}
                        aria-label={`${label} minimum`}
                    />
                    <SliderPrimitive.Thumb
                        className="block h-4 w-4 rounded-full bg-fa-cream border-2 border-black shadow cursor-grab active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fa-cream/50"
                        data-testid={`${testid || `range-${id}`}-max-thumb`}
                        aria-label={`${label} maximum`}
                    />
                </SliderPrimitive.Root>
                <input
                    type="number"
                    min={localMin}
                    max={maxValue}
                    step={step}
                    value={localMax}
                    onChange={(e) => setLocalMax(Number(e.target.value))}
                    onBlur={(e) => commitMax(e.target.value)}
                    data-testid={`${testid || `range-${id}`}-max-input`}
                    className="w-20 bg-black/60 border border-white/10 focus:border-fa-cream/60 outline-none text-white font-mono text-xs px-2 py-1 tabular-nums"
                    aria-label={`${label} maximum`}
                />
            </div>

            <div className="flex items-center justify-between font-mono text-[10px] text-white/55">
                <span data-testid={`${testid || `range-${id}`}-display-min`}>
                    {format(localMin, fmt, unit)}
                </span>
                <span className="text-white/30">–</span>
                <span data-testid={`${testid || `range-${id}`}-display-max`}>
                    {format(localMax, fmt, unit)}
                </span>
            </div>
        </div>
    );
}

/** Helper: returns true iff value lies within [selectedMin, selectedMax]. */
export function passesRange(value, filter) {
    if (!filter || !filter.isEnabled) return true;
    if (value === undefined || value === null) return false;
    return value >= filter.selectedMin && value <= filter.selectedMax;
}
